import i18next from "i18next";

import {
  getIncentivesByPlatformIdReferralRewardsByPersonIdRedeemable,
  getIncentivesByPlatformLocationIdAvailableReferralReward,
  postReferralCodesPersonalCodeByPersonId,
  postReferralCodesStatelessShareUrlByPersonalReferralCodePlatformByPlatformShareInvite,
} from "api/methods";

import { Models } from "@/api/types";
import { formatPlatformNameForReferral } from "@/organisms/refer-a-friend/utils";

import {
  getSignupRewardDiscountType,
  getSignupRewardInclusions,
  getSignupRewardTitle,
} from "../rewards/rewards.utils";

export const getAvailableReferralRewardForPlatformLocation =
  getIncentivesByPlatformLocationIdAvailableReferralReward;

export const generatePersonalCode = postReferralCodesPersonalCodeByPersonId;

export const generateStatelessShareCode =
  postReferralCodesStatelessShareUrlByPersonalReferralCodePlatformByPlatformShareInvite;

export const getAwardedReferralRewards =
  getIncentivesByPlatformIdReferralRewardsByPersonIdRedeemable;

function displayAvailableReferralRewardTitleText(
  referralReward: Models<"DisplayAvailableReferralReward">,
): string {
  return referralReward.rewardTitle?.text?.trim() ?? "";
}

type GenerateReferralShareTextParams = {
  platformName: string;
  rewardTitle: string;
  locationName: string;
  urlToShare: string;
  /**
   * Optional per-location share copy template configured for the user's platform
   * location. Supports `{{locationName}}`, `{{platformName}}`, and `{{rewardTitle}}`
   * placeholders. When `null`/`undefined`/empty, falls back to the default copy.
   */
  template?: string | null;
};

const getDefaultReferralShareCopyTemplate = (): string =>
  i18next.t("referralRewardsUtils.defaultShareCopyTemplate");

const renderReferralShareTemplate = (
  template: string,
  values: { locationName: string; platformName: string; rewardTitle: string },
): string =>
  template
    .replaceAll("{{locationName}}", values.locationName)
    .replaceAll("{{platformName}}", values.platformName)
    .replaceAll("{{rewardTitle}}", values.rewardTitle);

export const generateReferralShareText = ({
  platformName,
  rewardTitle,
  locationName,
  urlToShare,
  template,
}: GenerateReferralShareTextParams): string => {
  const formattedPlatformName = formatPlatformNameForReferral(platformName) ?? platformName;

  const effectiveTemplate =
    template && template.trim().length > 0 ? template : getDefaultReferralShareCopyTemplate();

  const renderedBody = renderReferralShareTemplate(effectiveTemplate, {
    locationName,
    platformName: formattedPlatformName,
    rewardTitle,
  });

  return i18next.t("referralRewardsUtils.shareTextWithClaimLink", { renderedBody, urlToShare });
};

export function getReferralRewardTitle(
  referralReward?: Models<"DisplayAvailableReferralReward"> | null,
  activeSignupReward?: Models<"DisplayActiveReward"> | null,
) {
  if (!referralReward || !activeSignupReward) {
    return i18next.t("referralRewardsUtils.referralRewardsFallbackTitle");
  }

  const itemName = displayAvailableReferralRewardTitleText(referralReward);
  if (!itemName) {
    return i18next.t("referralRewardsUtils.referralRewardsFallbackTitle");
  }

  if (referralReward.referralRewardType === "RepeatCash") {
    return i18next.t("referralRewardsUtils.easiestItemYouWillEarn", { itemName });
  }

  if (referralReward.referralRewardType === "ComplimentaryItem") {
    return i18next.t("referralRewardsUtils.easiestItemYouWillEarn", { itemName });
  }

  if (referralReward.referralRewardType === "InOfficeReward") {
    return i18next.t("referralRewardsUtils.easiestWayToEarn");
  }
}

export function getReferralRewardDescription(
  referralReward?: Models<"DisplayAvailableReferralReward"> | null,
  signupRewardText = i18next.t("referralRewardsUtils.defaultSignupRewardText"),
) {
  if (!referralReward || !signupRewardText) {
    return i18next.t("referralRewardsUtils.seeReferralHistory");
  }

  const itemName = displayAvailableReferralRewardTitleText(referralReward);
  if (!itemName) {
    return i18next.t("referralRewardsUtils.seeReferralHistory");
  }

  if (referralReward.referralRewardType === "RepeatCash") {
    return i18next.t("referralRewardsUtils.bothWinRepeatCash", { itemName, signupRewardText });
  }

  if (referralReward.referralRewardType === "ComplimentaryItem") {
    return i18next.t("referralRewardsUtils.bothWinComplimentaryItem", {
      itemName,
      signupRewardText,
    });
  }

  if (referralReward.referralRewardType === "InOfficeReward") {
    return i18next.t("referralRewardsUtils.bothWinInOffice", { itemName, signupRewardText });
  }
}

export const getReferralRewardCardTitle = (
  referralReward?: Models<"DisplayAvailableReferralReward"> | null,
) => {
  if (!referralReward) {
    return i18next.t("referralRewardsUtils.referralHistoryFallbackTitle");
  }

  const repeatCashAmount = Number(referralReward.repeatCashAmountInCents ?? 0) / 100;

  if (referralReward.referralRewardType === "RepeatCash") {
    return i18next.t("referralRewardsUtils.easiestRepeatCashYouWillEarn", { repeatCashAmount });
  }

  if (referralReward.referralRewardType === "ComplimentaryItem") {
    return i18next.t("referralRewardsUtils.easiestWayToEarn");
  }

  if (referralReward.referralRewardType === "InOfficeReward") {
    return i18next.t("referralRewardsUtils.rewardedForBringingFriend");
  }
};

export const getReferralRewardCardDescription = (
  referralReward?: Models<"DisplayAvailableReferralReward"> | null,
  signupReward?: Models<"DisplayActiveReward"> | null,
) => {
  if (!referralReward || !signupReward) {
    return i18next.t("referralRewardsUtils.seePastReferrals");
  }

  const signupRewardDiscountType = getSignupRewardDiscountType(signupReward);

  const repeatCashAmount = Number(referralReward.repeatCashAmountInCents ?? 0) / 100;

  const amountOff = Number(signupReward.discount?.amountOff ?? 0) / 100;

  const discountPercentOff = Number(signupReward.discount?.percentOff ?? 0) * 100;

  const titledRewardLabel = displayAvailableReferralRewardTitleText(referralReward);
  // The non-RepeatCash branches and the trailing fallback all interpolate
  // `titledRewardLabel` directly, so an empty value would render
  // grammatically broken copy like "You'll get a  and they'll get $10 off."
  // Match the early-return pattern used by getReferralRewardTitle and
  // getReferralRewardDescription above.
  if (referralReward.referralRewardType !== "RepeatCash" && !titledRewardLabel) {
    return i18next.t("referralRewardsUtils.seePastReferrals");
  }

  // Repeat Cash
  if (
    referralReward.referralRewardType === "RepeatCash" &&
    signupRewardDiscountType === "AmountOff"
  ) {
    return i18next.t("referralRewardsUtils.bothWinRepeatCashAmountOff", {
      repeatCashAmount,
      amountOff,
    });
  }

  if (
    referralReward.referralRewardType === "RepeatCash" &&
    signupRewardDiscountType === "PercentOff"
  ) {
    return i18next.t("referralRewardsUtils.bothWinRepeatCashPercentOff", {
      repeatCashAmount,
      discountPercentOff,
    });
  }

  if (
    referralReward.referralRewardType === "RepeatCash" &&
    (signupRewardDiscountType === "FreeItemAll" || signupRewardDiscountType === "FreeItemSingle")
  ) {
    return i18next.t("referralRewardsUtils.bothWinRepeatCashFreeItems", { repeatCashAmount });
  }

  if (
    referralReward.referralRewardType === "RepeatCash" &&
    signupRewardDiscountType === "FreeItemChooseFrom"
  ) {
    return i18next.t("referralRewardsUtils.bothWinRepeatCashFreeItemChooseFrom", {
      repeatCashAmount,
    });
  }

  // Complimentary Item and In-Office rewards share the same copy shape,
  // interpolating `titledRewardLabel` in place of the RepeatCash amount.
  if (
    (referralReward.referralRewardType === "ComplimentaryItem" ||
      referralReward.referralRewardType === "InOfficeReward") &&
    signupRewardDiscountType === "AmountOff"
  ) {
    return i18next.t("referralRewardsUtils.bothWinItemAmountOff", {
      titledRewardLabel,
      amountOff,
    });
  }

  if (
    (referralReward.referralRewardType === "ComplimentaryItem" ||
      referralReward.referralRewardType === "InOfficeReward") &&
    signupRewardDiscountType === "PercentOff"
  ) {
    return i18next.t("referralRewardsUtils.bothWinItemPercentOff", {
      titledRewardLabel,
      discountPercentOff,
    });
  }

  if (
    (referralReward.referralRewardType === "ComplimentaryItem" ||
      referralReward.referralRewardType === "InOfficeReward") &&
    (signupRewardDiscountType === "FreeItemAll" || signupRewardDiscountType === "FreeItemSingle")
  ) {
    return i18next.t("referralRewardsUtils.bothWinItemFreeItems", { titledRewardLabel });
  }

  if (
    (referralReward.referralRewardType === "ComplimentaryItem" ||
      referralReward.referralRewardType === "InOfficeReward") &&
    signupRewardDiscountType === "FreeItemChooseFrom"
  ) {
    return i18next.t("referralRewardsUtils.bothWinItemFreeItemChooseFrom", {
      titledRewardLabel,
    });
  }

  return i18next.t("referralRewardsUtils.bothWinFallback", {
    titledRewardLabel,
    signupRewardTitle: signupReward.title,
  });
};

export const getReferralRewardCurrentPatientRewardTitle = (
  referralReward: Models<"DisplayAvailableReferralReward">,
) => {
  if (referralReward.referralRewardType === "RepeatCash") {
    return i18next.t("referralRewardsUtils.repeatCashForReferring", {
      repeatCashAmount: (referralReward.repeatCashAmountInCents || 0) / 100,
    });
  }

  // Match the empty-title fallback pattern used elsewhere in this file so
  // the UI doesn't render copy like "Free  for referring a friend." or
  // an entirely blank string when the API omits the reward title.
  const titledRewardLabel = displayAvailableReferralRewardTitleText(referralReward);
  if (!titledRewardLabel) {
    return i18next.t("referralRewardsUtils.referralRewardFallback");
  }

  if (referralReward.referralRewardType === "ComplimentaryItem") {
    return i18next.t("referralRewardsUtils.freeItemForReferring", { titledRewardLabel });
  }

  return `${titledRewardLabel}`;
};

export const getReferralRewardNewPatientRewardTitle = (
  signupReward: Models<"DisplayActiveReward">,
  referrerName: string,
) => {
  const suffix = getSignupRewardTitle(signupReward);

  return i18next.t("referralRewardsUtils.rewardThanksToReferrer", { suffix, referrerName });
};
